<template>
  <div>
    <div class="row">
      <div class="col-12 mb-3 mt-1">
        <app-form-sub-title>
          {{ $t('app.listGoods') }}
          <template v-slot:right-content>
            <b-button-group>
              <b-button :disabled="disabledAdd" variant="outline-primary" size="sm" class="ml-3 text-nowrap"
                @click="onClickAdd(PRODUCT_TYPE.PRODUCT)">
                <i class="fal fa-plus mr-1"></i>
                {{ $t('app.addNewRow') }}
              </b-button>
              <b-dropdown variant="outline-primary" :disabled="disabledAdd" right>
                <b-dropdown-item @click="onClickAdd(PRODUCT_TYPE.DISCOUNT)">
                  <i class="fal fa-tag mr-1"></i>
                  {{ $t('app.addDiscount') }}
                </b-dropdown-item>
                <b-dropdown-item @click="onClickAdd(PRODUCT_TYPE.NOTE)">
                  <i class="fal fa-comment fa-flip-horizontal mr-1"></i>
                  {{ $t('app.addNote') }}
                </b-dropdown-item>
              </b-dropdown>
            </b-button-group>
          </template>
        </app-form-sub-title>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div style="border-bottom: 1px solid rgba(0, 0, 0, 0.1)"></div>
      </div>
      <div class="col-12 form-group">
        <vue-good-table class="table-container" :rows="rowWithoutAdditional" :columns="columns" :line-numbers="true">
          <template slot="table-row" slot-scope="props">
            <span
              v-if="data[props.row.id].type === PRODUCT_TYPE.NOTE && !['type', 'name', 'unit', 'price', 'quantity', 'action'].includes(props.column.field)"></span>
            <span
              v-else-if="data[props.row.id].type === PRODUCT_TYPE.DISCOUNT && props.column.field === 'method'"></span>
            <span v-else-if="props.column.field === 'type'">
              {{ renderTypeCell(props.row) }}
            </span>
            <span v-else-if="props.column.field === 'total'">
              {{ tempNumberFormatterFn(data[props.row.id][props.column.field], props.column.round,
                separatorFromSettings.thousandSeparator, separatorFromSettings.decimalSeparator) }}
              {{ props.column.currency }}
            </span>
            <div class="d-flex gap-8" v-else-if="props.column.field === 'rate'">
              <b-form-select :id="props.row.id.toString()" style="display: inline-block" :options="taxRateOptions"
                name="tax-rate" v-model="data[props.row.id]['rate']"
                @change="onChangeTaxRate(props.row, props.column, $event)" />
              <b-form-input :id="props.row.id.toString()" v-if="data[props.row.id]['rate'] === 'KHAC'"
                style="display: inline-block; width: 90px" type="number" name="custom-tax" min="0" max="100"
                v-model.number="data[props.row.id]['custom_tax_rate']"
                @input="onChangeCustomTaxRate(props.row, { field: 'custom_tax_rate' }, $event)" />
            </div>
            <div v-else-if="props.column.field === 'method'">
              <b-form-select name="method" :id="props.row.id.toString()" :options="methodOptions"
                :value="data[props.row.id]['method']" v-model="data[props.row.id][props.column.field]"
                @change="onChangeMethod(props.row, data[props.row.id]['method'])">
              </b-form-select>
            </div>
            <div v-else-if="props.column.field === 'name'">
              <div class="product-name-cell">
                <textarea style="font-size: 90%" :rows="1" class="form-control product-name-textarea" :max-rows="3" :name="props.column.field"
                  :ref="`name-input-${props.row.id}`"
                  :id="props.column.field + `-` + props.row.id.toString()"
                  v-model="data[props.row.id][props.column.field]"
                  @input="onNameInput(props.row, $event.target.value)"
                  @keydown.enter.prevent="onNameEnter(props.row)"
                  @focus="onNameFocus(props.row, props.column)"
                  @blur="onNameBlur(props.row.id)"
                  :placeholder="`${$t('app.enter')}...`">
                </textarea>
                <div
                  v-if="shouldShowProductSuggestions(props.row.id)"
                  class="product-suggestion-dropdown"
                  :style="getSuggestionDropdownStyle(props.row.id)"
                  @mousedown.prevent
                >
                  <div
                    v-for="(item, index) in getProductSuggestions(props.row.id)"
                    :key="item.id || `${props.row.id}-${index}`"
                    class="product-suggestion-item"
                    @click="selectProductSuggestion(props.row, item)"
                  >
                    <strong>{{ item.name || '' }}</strong>
                    <span class="ml-1 text-muted">{{ renderSuggestionMeta(item) }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-else-if="props.column.field === 'unit'">
              <!-- :id="props.row.id.toString()" -->
              <b-form-input :name="props.column.field" :list="props.column.field + `-` + props.row.id"
                :id="props.column.field + `-` + props.row.id.toString()" style="display: inline-block"
                v-model="data[props.row.id][props.column.field]"
                @focus="removeInvalidMarker(props.column.field, props.row.id)">
              </b-form-input>
              <datalist :id="props.column.field + `-` + props.row.id">
                <option :key="option.value" :value="option.value" v-for="option in unitOptions">{{ option.text }}
                </option>
              </datalist>
            </div>
            <div v-else-if="props.column.field === 'action'">
              <button :id="props.row.id.toString()" @click="onRemoveRow(props.row.id)" class="btn btn-sm btn-primary">
                <i class="far fa-trash"></i>
              </button>
            </div>
            <div v-else>
              <form-currency :min="handleMinCurrency(props.row)" :ref="props.column.field + `-` + props.row.id"
                :name="props.column.field" :currency="props.column.currency" :is-auto-catch="true"
                :disabled="disableFieldByRowType(props.column.field, data[props.row.id])"
                :fraction-digits="props.column.round" :value="data[props.row.id][props.column.field]"
                :thousand-separator="separatorFromSettings.thousandSeparator"
                :decimal-separator="separatorFromSettings.decimalSeparator"
                @input="(event, model) => onInput(props.row, props.column, model)">
              </form-currency>
            </div>
          </template>
        </vue-good-table>
      </div>
    </div>
    <transition name="slide-fade">
      <b-alert class="mt-3" v-model="showAlert" variant="warning" dismissible>
        <i class="fas fa-exclamation-triangle"></i>
        Bạn phải chọn mức thuế suất chiết khấu bằng với mức thuế suất mà mặt hàng cần chiết khấu!
      </b-alert>
    </transition>
    <transition name="slide-fade">
      <div class="additional-info mt-2" v-if="data.length && invoiceType === '2'">
        <div class="d-flex flex-col gap-12">
          <div class="d-flex flex-row gap-12 align-items-center">
            <span class="text-primary font-weight-bold">Giảm thuế theo NĐ số 204/2025</span>
            <p class="mb-0 text-error" style="font-size: 12px"><strong class="font-weight-bold">Lưu ý:</strong> Chỉ thực
              hiện sau khi đã viết xong hết hoá đơn</p>
          </div>
          <div class="grid grid-cols-2 gap-8">
            <div>
              <label style="font-size: 13px; color: #005DCA">Thuế xuất</label>
              <div class="d-flex gap-8">
                <b-form-select size="sm" :options="additionalRateOption" v-model="rowAdditional['rate']"
                  @change="onChangeTaxRate(rowAdditional, { field: 'rate' }, rowAdditional['rate'])" />
                <b-form-input v-if="rowAdditional['rate'] === 'KHAC'" size="sm" type="number" min="0" max="100"
                  :value="rowAdditional['custom_tax_rate']" v-model.number="rowAdditional['custom_tax_rate']"
                  @input="onChangeCustomTaxRate(rowAdditional, { field: 'custom_tax_rate' }, $event)" />
              </div>
            </div>
            <div>
              <form-currency label="Tiền giảm" ref="taxMoney" name="taxMoney" :currency="this.currencyCode"
                :fraction-digits="this.handleMakeARound('TAX_MONEY')" :value="rowAdditional['tax_money']"
                @input="(event, model) => onInput(rowAdditional, { field: 'tax_money' }, model)">
              </form-currency>
            </div>
          </div>
        </div>
      </div>
    </transition>
    <div v-if="data.length">
      <div class="total-container">
        <hr v-if="isVatInvoice" />
        <div v-if="isVatInvoice" class="total-content">
          <div class="total">
            <label>{{ $t('invoice.totalAmount') }}</label>
            <span>{{ tempNumberFormatterFn(totals.total_amount_without_tax, roundSettings.TOTAL_MONEY,
              separatorFromSettings.thousandSeparator, separatorFromSettings.decimalSeparator) }}
              {{ currencyCode }}</span>
          </div>
          <div class="total">
            <label>{{ $t('invoice.taxAmount') }}</label>
            <span>{{ tempNumberFormatterFn(totals.total_tax_mount, roundSettings.TAX_GTGT_MONEY,
              separatorFromSettings.thousandSeparator, separatorFromSettings.decimalSeparator) }}
              {{ currencyCode }}</span>
          </div>
        </div>
        <hr />
        <div class="grand-total">
          <div class="total">
            <label>{{ $t('invoice.grandTotal') }}</label>
            <span class="d-inline-flex">
              <span v-if="!!renderIncreaseOrDecrease" class=" mb-0 mr-2">
                {{ renderIncreaseOrDecrease }}:
              </span>
              {{ tempNumberFormatterFn(derivedTotalFromTaxAndDecree, roundSettings.SUM_LOADS_MONEY,
                separatorFromSettings.thousandSeparator, separatorFromSettings.decimalSeparator) }} {{ currencyCode }}
            </span>
          </div>
        </div>
        <hr />
        <div class="total-in-word">
          <div class="total">
            <label>{{ $t('invoice.totalInWords') }}</label>
            <span>{{ totalAmountWithTaxInWords }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import 'vue-good-table/dist/vue-good-table.css';
import { mapState } from 'vuex';
import FormCurrency from '@/components/app-form/fields/form-currency';
import AppFormSubTitle from '@/components/app-form/form-sub-title';
import { VueGoodTable } from 'vue-good-table';
import { PRODUCT_TYPE } from '@/app-constant';
import NumericUtils from '@/utils/numeric';
import { makeARound } from '@/utils/invoice';
import { debounce } from 'lodash';
import { tempNumberFormatter } from '@/utils/numeric';
import { convertNumberToWords } from '@/utils/text';
import ProductProxy from '@/store/proxies/product-proxy';
import ProductCategoryProxy from '@/store/proxies/product-category-proxy';

// TODO: make this an IIFE
// const roundSettings = ctx => ({
//   SUM_LOADS_MONEY: makeARound('SUM_LOADS_MONEY', 'on_invoice__settings', ctx.currencyCode),
//   TAX_MONEY: makeARound('TAX_MONEY'),
//   // TAX_GTGT_MONEY: makeARound('TAX_GTGT_MONEY', 'on_invoice__settings', ctx.currencyCode),
//   TOTAL_MONEY: makeARound('TOTAL_MONEY', 'on_invoice__settings', ctx.currencyCode),
//   SUB_TOTAL: makeARound('SUB_TOTAL', 'on_detail_invoice__settings', ctx.currencyCode),
// });

const roundSettings = ctx => ({
  // Chi tiết hóa đơn (on_detail_invoice__settings)
  SUB_TOTAL: makeARound('SUB_TOTAL', 'on_detail_invoice__settings', ctx.currencyCode),
  TAX_MONEY: makeARound('TAX_MONEY', 'on_detail_invoice__settings', ctx.currencyCode),

  // Tổng hóa đơn (on_invoice__settings)
  TOTAL_MONEY: makeARound('TOTAL_MONEY', 'on_invoice__settings', ctx.currencyCode), // tổng trước thuế
  TAX_GTGT_MONEY: makeARound('TAX_GTGT_MONEY', 'on_invoice__settings', ctx.currencyCode),
  CHIET_KHAU_MONEY: makeARound('CHIET_KHAU_MONEY', 'on_invoice__settings', ctx.currencyCode),
  SUM_LOADS_MONEY: makeARound('SUM_LOADS_MONEY', 'on_invoice__settings', ctx.currencyCode), // tổng thanh toán (sau thuế)
});


const vatInvoice = '1';
const VND = 'VND';
const timeoutDebounce = 300;

/*
TODO: toggle ẩn hiện mức thuế sản phẩm

- chỉ set total_tax_mount = 0 trong getTotals() nếu invoiceType = 2 | 6
- chỉ set rate, custom_tax_rate và tax_money của các dòng sp = 0 trong getTotals() nếu invoiceType = 2 | 6
*/

export default {
  name: 'dynamic-table',
  components: {
    VueGoodTable,
    FormCurrency,
    AppFormSubTitle,
  },
  data() {
    return {
      PRODUCT_TYPE,
      additionalRateOption: [
        { value: 1, text: '1%' },
        { value: 2, text: '2%' },
        { value: 3, text: '3%' },
        { value: 5, text: '5%' },
        { value: 'KHAC', text: 'Khác' },
      ],
      showAlert: false,
      roundSettings: roundSettings(this),
      totals: {
        decree_tax_reduction: 0,
        total_amount_with_tax: 0,
        total_amount_without_tax: 0,
        total_tax_mount: 0
      },
      productCategories: [],
      productCategoryMap: {},
      productSearchStates: {},
      productSearchDebouncers: {},
      defaultProductSuggestions: [],
      defaultProductSuggestionsPromise: null,
    };
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    invoiceType: {
      type: String,
      default: vatInvoice,
    },
    disabledAdd: {
      type: Boolean,
      default: false,
    },
    currencyCode: {
      type: String,
      default: VND,
    },
    sellerSettingList: {
      type: Object,
      default: () => { },
    },
    isAdjustment: {
      type: Boolean,
      default: false,
    },
    cached_total_amount_with_tax: {
      type: Number,
      default: 0,
    },
    cached_total_amount_without_tax: {
      type: Number,
      default: 0,
    },
    cached_total_tax_mount: {
      type: Number,
      default: 0,
    }
  },
  mounted() {
    console.log('roundSettings resolved:', this.roundSettings);
  },
  created() {
    // TODO: Nhờ a Bảy thêm logic giúp là nếu invoice type là 2 thì cho total_amount_with_tax = total_amount_without_tax
    this.totals = {
      ...this.totals,
      total_amount_with_tax: Number(this.cached_total_amount_with_tax),
      total_amount_without_tax: Number(this.cached_total_amount_without_tax),
      total_tax_mount: Number(this.cached_total_tax_mount),
    }

    this.$bus.on('autofill-products-from-template', this.addTotalsFromTemplate)
    this.fetchProductCategories();
    window.addEventListener('resize', this.refreshSuggestionPositions);
    window.addEventListener('scroll', this.refreshSuggestionPositions, true);
  },
  beforeDestroy() {
    this.$bus.off('autofill-products-from-template', this.addTotalsFromTemplate)
    Object.values(this.productSearchDebouncers).forEach((fn) => {
      if (fn && fn.cancel) fn.cancel();
    });
    window.removeEventListener('resize', this.refreshSuggestionPositions);
    window.removeEventListener('scroll', this.refreshSuggestionPositions, true);
  },
  computed: {
    ...mapState(['invoiceListEnum']),
    derivedTotalFromTaxAndDecree() {
      return this.totals.total_amount_with_tax - this.totals.decree_tax_reduction
    },
    totalAmountWithTaxInWords() {
      return convertNumberToWords(
        this.derivedTotalFromTaxAndDecree,
        this.currencyCode,
        this.isAdjustment,
        this.separatorFromSettings.decimalSeparator
      )
    },
    methodOptions() {
      return [
        { value: 'TANG', text: this.$t('invoice.method.TANG') },
        { value: 'GIAM', text: this.$t('invoice.method.GIAM') },
      ];
    },
    taxRateOptions() {
      return [
        { value: 0, text: '0%' },
        { value: 5, text: '5%' },
        { value: 8, text: '8%' },
        { value: 10, text: '10%' },
        { value: 'KHAC', text: 'Khác' },
        { value: 'NONE', text: 'Không tính thuế' },
        { value: 'KKKNT', text: 'KKKNT' },
      ];
    },
    columns() {
      let baseColumns = [
        {
          label: 'Điều chỉnh',
          field: 'method',
          width: '120px',
          hidden: !this.isAdjustment,
        },
        {
          label: this.$t('invoice.type'),
          field: 'type',
          width: '60px',
          formatFn: this.transformType,
        },
        {
          label: this.$t('invoice.nameOfGoodsAndServices'),
          field: 'name',
          width: '200px',
        },
        {
          label: this.$t('invoice.unit'),
          field: 'unit',
          width: '80px',
        },
        {
          label: this.$t('invoice.quantity'),
          field: 'quantity',
          width: '80px',
          round: this.handleMakeARound('QUANTITY', 'qty_rate__settings'),
        },
        {
          label: this.$t('invoice.price'),
          field: 'price',
          width: '140px',
          currency: this.currencyCode,
          round: this.handleMakeARound('UNIT_PRICE'),
        },
        {
          label: `${this.$t('invoice.subtotal')}`,
          field: 'subtotal',
          width: '160px',
          currency: this.currencyCode,
          round: this.handleMakeARound('SUB_TOTAL'),
        },
        {
          label: `${this.$t('invoice.rate')} (%)`,
          field: 'rate',
          width: '155px',
        },
        {
          label: this.$t('invoice.taxMoney'),
          field: 'tax_money',
          width: '130px',
          currency: this.currencyCode,
          round: this.handleMakeARound('TAX_MONEY'),
        },
        {
          label: this.$t('invoice.total'),
          field: 'total',
          width: '160px',
          currency: this.currencyCode,
          round: this.handleMakeARound('SUM_LOADS_MONEY', 'on_invoice__settings'),
        },
        {
          label: '',
          field: 'action',
          type: 'action',
          width: '40px',
        },
      ];
      if (this.invoiceType !== vatInvoice) {
        baseColumns = baseColumns.filter(c => c.field !== 'rate' && c.field !== 'tax_money');
      }
      if (!this.isAdjustment) baseColumns = baseColumns.filter(c => c.field !== 'method');

      return baseColumns;
    },
    // rows() {
    //   const grouped = this.data.reduce((acc, item) => {
    //     const typeKey = item.type || PRODUCT_TYPE.PRODUCT;
    //     if (acc[typeKey]) {
    //       acc[typeKey].push(item);
    //     }
    //     return acc;
    //   }, {
    //     [this.PRODUCT_TYPE.PRODUCT]: [],
    //     [this.PRODUCT_TYPE.NOTE]: [],
    //     [this.PRODUCT_TYPE.DISCOUNT]: [],
    //   });
    //   return Object.keys(grouped).map(key => ({
    //     mode: 'span',
    //     label: this.$t(`invoice.productTypes.${key}`) || key,
    //     html: true,
    //     children: grouped[key],
    //   })).filter(row => row.children.length > 0);
    // },
    isVatInvoice() {
      return this.invoiceType === vatInvoice;
    },
    isChosenMethod() {
      return this.data.some(item => item.method !== null);
    },
    // totals() {
    //   return handleTotals(this.data, this.currencyCode, this.invoiceListEnum);
    // },
    renderIncreaseOrDecrease() {
      if (this.isChosenMethod && this.isAdjustment) {
        const total = NumericUtils.toNumber(this.totals.total_amount_with_tax);
        if (total > 0) return this.$t('invoice.method.TANG');
        if (total < 0) return this.$t('invoice.method.GIAM');
      }
      return '';
    },
    rowWithoutAdditional() {
      return this.data.filter(item => item.type !== PRODUCT_TYPE.DECREE);
    },
    rowAdditional() {
      return this.data.filter(item => item.type === PRODUCT_TYPE.DECREE)[0] || {};
    },
    unitOptions() {
      let result = [];
      if (this.sellerSettingList && this.sellerSettingList.list_unit_name && this.sellerSettingList.list_unit_name.length > 0) {
        result = this.sellerSettingList.list_unit_name.map(item => ({ text: item, value: item }));
      }
      return result;
    },
    separatorFromSettings() {
      if (this.sellerSettingList) {
        let [decimalSeparator, thousandSeparator] = this.sellerSettingList.seprator__settings || [',', '.'];
        decimalSeparator = decimalSeparator.text_val ? decimalSeparator.text_val : decimalSeparator
        thousandSeparator = thousandSeparator.text_val ? thousandSeparator.text_val : thousandSeparator
        return { decimalSeparator, thousandSeparator }
      } else return { decimalSeparator: ',', thousandSeparator: '.' }
    }
  },
  methods: {
    getTotals() {
      if (this.isAdjustTaxOnlyMode()) {
        const rows = Array.isArray(this.data) ? this.data : [];
        const tax = rows.reduce((s, l) => {
          if (l.type === PRODUCT_TYPE.NOTE || l.type === PRODUCT_TYPE.DECREE) return s;
          return s + Number(l.tax_money || 0);
        }, 0);

        return {
          ...this.totals,
          total_amount_without_tax: 0,
          total_amount_with_tax: 0,
          total_tax_mount: tax,
          total_amount_with_tax_in_words: this.totalAmountWithTaxInWords
        };
      }

      return new Object({
        ...this.totals,
        total_amount_with_tax: this.derivedTotalFromTaxAndDecree,
        total_amount_with_tax_in_words: this.totalAmountWithTaxInWords
      });
    },
    disableFieldByRowType(field, row) {
      // Với ADJUST_INCREASE_DECREASE, cho phép nhập tay subtotal, tax_money và rate
      if (this.isAdjustment && [PRODUCT_TYPE.PRODUCT, PRODUCT_TYPE.DISCOUNT].includes(row.type)) {
        if (field === 'subtotal' || field === 'tax_money' || field === 'rate') return false;
      }

      if (field === 'subtotal') return true;

      if ([PRODUCT_TYPE.PRODUCT, PRODUCT_TYPE.DISCOUNT].includes(row.type)) {
        if (this.getTaxRate(row) === 0) return field === 'tax_money'
      }
      // else if ([PRODUCT_TYPE.NOTE, PRODUCT_TYPE.DECREE].includes(row.type)) return false
      return false
    },
    tempNumberFormatterFn(...args) {
      return tempNumberFormatter.apply(this, args);
    },
    handleMinCurrency(row) {
      if (this.isAdjustment && row.method && row.method === 'GIAM') {
        return null;
      } return 0;
    },
    handleMakeARound(key = '', type = 'on_detail_invoice__settings') {
      return makeARound(key, type, this.currencyCode, this.sellerSettingList);
    },
    onClickAdd(type = PRODUCT_TYPE.PRODUCT) {
      if (type === PRODUCT_TYPE.DISCOUNT) {
        this.showAlert = true;
      }
      this.data.push({
        ...this.columns.reduce((obj, item) => Object.assign(obj, { [item.field]: null }), {}),
        name: type === PRODUCT_TYPE.DECREE ? '' : null,
        id: (this.data.length),
        product_id: null,
        custom_tax_rate: 0,
        rate: type === PRODUCT_TYPE.NOTE || type === PRODUCT_TYPE.DECREE ? 0 : 'NONE',
        type,
      });
    },
    onRemoveRow(rowId) {
      this.data = this.data.filter(x => (x.id !== rowId)).map((x, index) => ({ ...x, id: index }));
      this.recalculateTotals();
      this.passDataToParent();
    },

    onInput: debounce(function onDebouncedInput(row, col, model) {
      const currentRow = this.data[row.id];
      // Gán giá trị mới cho cell
      currentRow[col.field] =
        model && (model.value !== null && model.value !== undefined)
          ? (row.method === 'GIAM' && model.value > 0 ? -model.value : model.value)
          : null;

      currentRow.quantity = parseFloat(currentRow.quantity || 0);
      currentRow.price = parseFloat(currentRow.price || 0);

      // Với ADJUST_INCREASE_DECREASE, cho phép nhập tay subtotal và tax_money
      if (this.isAdjustment && [PRODUCT_TYPE.PRODUCT, PRODUCT_TYPE.DISCOUNT].includes(row.type)) {
        if (col.field === 'subtotal') {
          // Khi nhập tay subtotal, không tính lại từ quantity * price
          const subtotal = parseFloat(currentRow.subtotal || 0);
          const taxMoney = parseFloat(currentRow.tax_money || 0);
          currentRow._raw_subtotal = subtotal;
          // Nếu subtotal = 0 thì total = 0, bất kể tax_money
          currentRow._raw_total = subtotal === 0 ? 0 : (subtotal + taxMoney);
          currentRow.subtotal = NumericUtils.formatAmountAsNumber(subtotal, this.roundSettings.SUB_TOTAL);
          currentRow.total = NumericUtils.formatAmountAsNumber(currentRow._raw_total, this.roundSettings.SUM_LOADS_MONEY);
          this.recalculateTotals();
          this.passDataToParent();
          return;
        } else if (col.field === 'tax_money') {
          // Khi nhập tay tax_money, không tự động tính lại từ rate
          const taxMoney = parseFloat(currentRow.tax_money || 0);
          const subtotal = parseFloat(currentRow.subtotal || 0);
          currentRow._raw_tax_money = taxMoney;
          // Nếu subtotal = 0 thì total = 0, bất kể tax_money
          currentRow._raw_total = subtotal === 0 ? 0 : (subtotal + taxMoney);
          currentRow.tax_money = NumericUtils.formatAmountAsNumber(taxMoney, this.roundSettings.TAX_MONEY);
          currentRow.total = NumericUtils.formatAmountAsNumber(currentRow._raw_total, this.roundSettings.SUM_LOADS_MONEY);
          this.recalculateTotals();
          this.passDataToParent();
          return;
        } else if (col.field === 'rate' || col.field === 'custom_tax_rate') {
          // Khi thay đổi rate, chỉ tính lại tax_money nếu user chưa nhập tay
          // Kiểm tra xem có phải chế độ chỉ thuế không
          if (this.isAdjustTaxOnlyMode()) {
            // Chế độ chỉ thuế: không tính lại từ rate
            this.calculateSubTotal(currentRow, false, false);
          } else {
            // Tính lại tax_money từ rate
            this.calculateSubTotal(currentRow, false, false);
          }
          this.recalculateTotals();
          this.passDataToParent();
          return;
        }
      }

      const isEditingSubtotal = col.field === 'subtotal' || col.field === 'tax_money';
      if (col.field !== 'tax_money') {
        this.calculateSubTotal(currentRow, false, isEditingSubtotal);
      }

      if (['price', 'quantity', 'rate', 'custom_tax_rate'].includes(col.field)) {
        this.calculateSubTotal(currentRow, false, false);
      } else if (col.field === 'subtotal') {
        const prevSubtotal = parseFloat(currentRow._raw_subtotal || 0);
        const quantity = parseFloat(currentRow.quantity || 0);
        const price = parseFloat(currentRow.price || 0);
        if (quantity && !price) {
          currentRow.price = prevSubtotal / quantity;
        } else if (price && !quantity) {
          currentRow.quantity = prevSubtotal / price;
        } else if (price && quantity) {
          currentRow.price = prevSubtotal / quantity;
        }
        this.calculateSubTotal(currentRow, false, true);
      } else if (col.field === 'tax_money') {
        const taxMoney = parseFloat(currentRow.tax_money || 0);
        const subtotal = parseFloat(currentRow.subtotal || 0);
        if (row.type === PRODUCT_TYPE.DECREE) {
          currentRow.total = taxMoney;
        }
        else if (isNaN(taxMoney) || !taxMoney) {
          currentRow.total = currentRow.subtotal;
          currentRow.rate = 'NONE';
          currentRow.custom_tax_rate = 0;
        }
        else {
          // Nếu subtotal = 0 thì total = 0, bất kể tax_money
          currentRow.total = subtotal === 0 ? 0 : (currentRow.subtotal + taxMoney);
        }
      }

      if (['price', 'tax_money', 'rate', 'quantity', 'subtotal', 'custom_tax_rate'].includes(col.field)) {
        this.recalculateTotals();
      }

      // --- Lưu lại thuế suất hiện tại cho dòng ---
      currentRow.prevTaxRate = this.getTaxRate(currentRow);

      // --- Đồng bộ dữ liệu ra ngoài ---
      this.passDataToParent();

    }, timeoutDebounce),
    addTotalsFromTemplate(products, totals = {
      total_amount_without_tax: 0,
      total_tax_mount: 0,
      total_amount_with_tax: 0
    }) {

      const prevWithout = parseFloat(this.totals.total_amount_without_tax) || 0;
      const prevTax = parseFloat(this.totals.total_tax_mount) || 0;
      const prevWith = parseFloat(this.totals.total_amount_with_tax) || 0;
      this.totals = {
        ...this.totals,
        total_amount_without_tax: prevWithout + (Number(totals.total_amount_without_tax) || 0),
        total_tax_mount: prevTax + (Number(totals.total_tax_mount) || 0),
        total_amount_with_tax: prevWith + (Number(totals.total_amount_with_tax) || 0),
      }
    },
    onChangeCustomTaxRate(row, col, value) {
      const model = {
        value: !['KHAC', 'KKKNT', 'NONE', 'KCT'].includes(value) ? Number(value) : value
      }
      this.onInput(row, col, model);
    },
    onChangeTaxRate(row, col, value) {

      if (value === 'KHAC') {
        row[col.field] = 'KHAC';
        if (!row.custom_tax_rate) row.custom_tax_rate = '';
        return;
      }

      const model = {
        value: !['KKKNT', 'NONE', 'KCT'].includes(value) ? Number(value) : value
      }

      this.onInput(row, col, model);
    },
    onChangeMethod(row, method) {
      const currentRow = this.data[row.id];
      this.calculateSubTotal(currentRow, true);
      this.recalculateTotals();
    },
    getTaxRate(row) {
      if (row.rate === 'KHAC') {
        return Number(row.custom_tax_rate) || 0;
      }
      if (!this.isAdjustTaxOnlyMode() && ['KKKNT', 'NONE', 'KCT'].includes(row.rate)) return 0;
      if (row.rate === undefined || row.rate === null) return 0;
      return this.invoiceType === vatInvoice || row.type === PRODUCT_TYPE.DECREE ? Number(row.rate) : 0;
    },
    calculateSubTotal(row, methodChange = false, subTotalChanged = false) {
      if (row.type === PRODUCT_TYPE.NOTE) return;

      const taxRate = this.getTaxRate(row);
      const formatAmountAsNumber = NumericUtils.formatAmountAsNumber;

      if (typeof row._raw_subtotal === 'undefined') row._raw_subtotal = 0;
      if (typeof row._raw_tax_money === 'undefined') row._raw_tax_money = 0;
      if (typeof row._raw_total === 'undefined') row._raw_total = 0;

      row.quantity = Number(row.quantity) || 0;
      row.price = Number(row.price) || 0;

      // Với ADJUST_INCREASE_DECREASE, cho phép quantity/price empty, sử dụng subtotal/tax_money đã nhập
      if (this.isAdjustment && [PRODUCT_TYPE.PRODUCT, PRODUCT_TYPE.DISCOUNT].includes(row.type)) {
        const subtotal = parseFloat(row.subtotal) || 0;
        const taxMoney = parseFloat(row.tax_money) || 0;

        // Nếu có subtotal hoặc tax_money đã nhập, sử dụng giá trị đó
        if (subtotal || taxMoney) {
          row._raw_subtotal = subtotal;
          row._raw_tax_money = taxMoney;
          // Nếu subtotal = 0 thì total = 0, bất kể tax_money
          row._raw_total = subtotal === 0 ? 0 : (subtotal + taxMoney);

          row.subtotal = formatAmountAsNumber(row._raw_subtotal, this.roundSettings.SUB_TOTAL);
          row.tax_money = formatAmountAsNumber(row._raw_tax_money, this.roundSettings.TAX_MONEY);
          row.total = formatAmountAsNumber(row._raw_total, this.roundSettings.SUM_LOADS_MONEY);
          return;
        }
      }

      // Chế độ chỉ thuế (tất cả subtotal = 0)
      if (this.isAdjustTaxOnlyMode() && row.type !== PRODUCT_TYPE.DECREE) {
        row._raw_subtotal = 0;
        row._raw_tax_money = Number(row.tax_money) || 0;
        row._raw_total = 0;

        row.subtotal = formatAmountAsNumber(0, this.roundSettings.SUB_TOTAL);
        row.tax_money = formatAmountAsNumber(row._raw_tax_money, this.roundSettings.TAX_MONEY);
        row.total = formatAmountAsNumber(0, this.roundSettings.SUM_LOADS_MONEY);
        return;
      }

      if (row.type !== PRODUCT_TYPE.DECREE &&
        ((!row.quantity && !row.price) || (isNaN(row.quantity) && isNaN(row.price)))) {
        row._raw_subtotal = 0;
        row._raw_tax_money = 0;
        row._raw_total = 0;

        row.subtotal = formatAmountAsNumber(0, this.roundSettings.SUB_TOTAL);
        row.tax_money = formatAmountAsNumber(0, this.roundSettings.TAX_MONEY);
        row.total = formatAmountAsNumber(0, this.roundSettings.SUM_LOADS_MONEY);
        return;
      }

      if (this.isAdjustment && methodChange) {
        if (!row.quantity && !row.price) {
          row.subtotal = row.method === 'GIAM' ? -Math.abs(row.subtotal) : Math.abs(row.subtotal);
        }
        row.price = row.method === 'GIAM' ? -Math.abs(row.price) : Math.abs(row.price);
        row.quantity = row.method === 'GIAM' ? row.quantity : Math.abs(row.quantity);
      }

      if (!subTotalChanged) {
        row._raw_subtotal = row.quantity * row.price;
      } else {
        row._raw_subtotal = Number(row.subtotal) || 0;
      }

      const effectiveTaxRate = (taxRate === null || isNaN(taxRate)) ? 0 : Number(taxRate);

      if (row.type === PRODUCT_TYPE.DECREE && this.$refs.taxMoney) {
        let totalBase = this.totals.total_amount_with_tax;

        if (this.invoiceType === '2') {
          totalBase = this.totals.total_amount_without_tax;
        }

        const calculateTotalForTax = (taxRate * totalBase) / 100;

        row._raw_tax_money = (20 * calculateTotalForTax) / 100;
        row._raw_total = row._raw_tax_money;

        if (row.rate !== 'KHAC') {
          row.rate = taxRate;
          row.custom_tax_rate = taxRate;
        }

        row.tax_money = formatAmountAsNumber(row._raw_tax_money, this.roundSettings.TAX_MONEY);
        row.total = formatAmountAsNumber(row._raw_total, this.roundSettings.SUM_LOADS_MONEY);

        this.$refs.taxMoney.setValue(row._raw_tax_money);
        return;
      } else {
        row._raw_tax_money = (effectiveTaxRate * row._raw_subtotal) / 100;
        // Nếu subtotal = 0 thì total = 0, bất kể tax_money
        if (row._raw_subtotal === 0) {
          row._raw_total = 0;
        } else {
          row._raw_total = formatAmountAsNumber(row._raw_subtotal, this.roundSettings.SUB_TOTAL) +
            formatAmountAsNumber(row._raw_tax_money, this.roundSettings.TAX_MONEY);
        }
      }

      row.subtotal = formatAmountAsNumber(row._raw_subtotal, this.roundSettings.SUB_TOTAL);
      row.tax_money = formatAmountAsNumber(row._raw_tax_money, this.roundSettings.TAX_MONEY);
      row.total = formatAmountAsNumber(row._raw_total, this.roundSettings.SUM_LOADS_MONEY);
    },
    recalculateTotals() {

      if (this.isAdjustTaxOnlyMode()) {
        let tax = 0;
        for (const r of this.data) tax += Number(r.tax_money || 0);

        const f = NumericUtils.formatAmountAsNumber;
        this.totals = {
          total_amount_without_tax: 0,
          total_tax_mount: f(tax, this.roundSettings.TAX_GTGT_MONEY),
          total_amount_with_tax: 0,
          decree_tax_reduction: 0
        };
        return;
      }

      let totalWithoutTax = 0;
      let totalTax = 0;
      let totalWithTax = 0;
      let decreeTaxReduction = 0;

      for (const row of this.data) {
        if (!row) continue;
        if (row.type === PRODUCT_TYPE.NOTE) continue;

        const subtotal = parseFloat(row.subtotal) || 0;
        const tax = parseFloat(row.tax_money) || 0;
        const total = parseFloat(row.total) || 0;

        if (row.type === PRODUCT_TYPE.PRODUCT) {
          totalWithoutTax += subtotal;
          totalTax += tax;
          totalWithTax += total;
        } else if (row.type === PRODUCT_TYPE.DISCOUNT) {
          totalWithoutTax -= subtotal;
          totalTax -= tax;
          totalWithTax -= total;
        } else if (row.type === PRODUCT_TYPE.DECREE) {
          decreeTaxReduction += total;
          totalWithTax += 0;
          totalWithoutTax += 0;
        }
      }
      const format = NumericUtils.formatAmountAsNumber;
      this.totals = {
        ...this.totals,
        total_amount_without_tax: format(totalWithoutTax, this.roundSettings.TOTAL_MONEY),
        total_tax_mount: format(totalTax, this.roundSettings.TAX_GTGT_MONEY),
        total_amount_with_tax: format(totalWithTax, this.roundSettings.SUM_LOADS_MONEY),
        decree_tax_reduction: format(decreeTaxReduction, this.roundSettings.TOTAL_MONEY)
      };
    },
    calculateTotals(type, subtotalDiff, taxMoneyDiff, totalDiff, decreeTaxReduction = 0, resetAll = false) {
      if (resetAll) {
        this.recalculateTotals();
        return;
      }

      const prevTotal = Number(this.totals.total_amount_with_tax) || 0;
      const prevSubtotal = Number(this.totals.total_amount_without_tax) || 0;
      const prevTax = Number(this.totals.total_tax_mount) || 0;

      const newTotals = {};
      const format = NumericUtils.formatAmountAsNumber;

      const sDiff = Number(subtotalDiff) || 0;
      const tDiff = Number(taxMoneyDiff) || 0;
      const totalDiffValue = Number(totalDiff) || 0;

      if (type === PRODUCT_TYPE.DISCOUNT) {
        newTotals.total_amount_without_tax = prevSubtotal - sDiff;
        newTotals.total_tax_mount = prevTax - tDiff;
        newTotals.total_amount_with_tax = prevTotal - totalDiffValue;
      }
      else if (type === PRODUCT_TYPE.DECREE) {
        newTotals.decree_tax_reduction = decreeTaxReduction || 0;
        newTotals.decree_tax_reduction = format(newTotals.decree_tax_reduction, this.roundSettings.TOTAL_MONEY);
        newTotals.total_tax_mount = 0;

        newTotals.total_amount_with_tax = prevTotal - decreeTaxReduction;
      }
      else {
        newTotals.total_amount_without_tax = prevSubtotal + sDiff;
        newTotals.total_tax_mount = prevTax + tDiff;
        if (this.isAdjustTaxOnlyMode()) {
          newTotals.total_amount_with_tax = 0;
        } else {
          newTotals.total_amount_with_tax = prevTotal + totalDiffValue;
        }
      }

      // Format cuối cùng
      this.totals = {
        ...this.totals,
        total_amount_without_tax: format(newTotals.total_amount_without_tax, this.roundSettings.TOTAL_MONEY),
        total_tax_mount: format(newTotals.total_tax_mount, this.roundSettings.TAX_GTGT_MONEY),
        total_amount_with_tax: format(newTotals.total_amount_with_tax, this.roundSettings.SUM_LOADS_MONEY),
        decree_tax_reduction: format(newTotals.decree_tax_reduction || 0, this.roundSettings.TOTAL_MONEY)
      };
    },
    passDataToParent(data = this.data) {
      this.$emit('onTableChange', data, Math.abs(this.derivedTotalFromTaxAndDecree - this.cached_total_amount_with_tax) > 0);
    },
    forceInvalidateFields(fields) {
      for (const [field, id] of fields) {
        if (['quantity', 'price', 'subtotal'].includes(field)) { // fields using form field mixins
          this.$refs[`${field}-${id}`].forceInvalidate();
        }
        else if (['name', 'unit', 'method'].includes(field)) { // fields using bootstrap vue comps (b-form-x)
          const classList = document.getElementById(`${field}-${id}`).classList
          if (!classList.contains('is-invalid')) {
            document.getElementById(`${field}-${id}`).classList.add('is-invalid')
          }
        }
      }
    },
    removeInvalidMarker(field, id) {
      if (['quantity', 'price', 'subtotal'].includes(field)) { // fields using form field mixins
        this.$refs[`${field}-${id}`].forceValidate();
      } else if (['name', 'unit', 'method'].includes(field)) {
        const classList = document.getElementById(`${field}-${id}`).classList
        if (classList.contains('is-invalid')) {
          classList.remove('is-invalid')
        }
      }
    },
    extractList(res) {
      if (Array.isArray(res)) return res;
      if (res && Array.isArray(res.results)) return res.results;
      if (res && Array.isArray(res.data)) return res.data;
      return [];
    },
    fetchProductCategories() {
      new ProductCategoryProxy()
        .list({ page: 1, page_size: 100 })
        .then((res) => {
          const categories = this.extractList(res);
          this.productCategories = categories;
          this.productCategoryMap = categories.reduce((acc, item) => {
            if (item && item.id != null) acc[item.id] = item.name || '';
            return acc;
          }, {});
        })
        .catch(() => {
          this.productCategories = [];
          this.productCategoryMap = {};
        });
    },
    getProductSearchState(rowId) {
      if (!this.productSearchStates[rowId]) {
        this.$set(this.productSearchStates, rowId, {
          keyword: '',
          suggestions: [],
          isOpen: false,
          requestKey: '',
          dropdownStyle: {},
        });
      }
      return this.productSearchStates[rowId];
    },
    getRowSearchDebouncer(rowId) {
      if (!this.productSearchDebouncers[rowId]) {
        this.productSearchDebouncers[rowId] = debounce((keyword) => {
          this.searchProductsForRow(rowId, keyword);
        }, timeoutDebounce);
      }
      return this.productSearchDebouncers[rowId];
    },
    onNameInput(row, keyword) {
      const currentRow = this.data[row.id];
      if (!currentRow) return;
      if (currentRow.name !== keyword) {
        currentRow.product_id = null;
      }
      currentRow.name = keyword;

      const state = this.getProductSearchState(row.id);
      if (currentRow.type !== PRODUCT_TYPE.PRODUCT) {
        state.suggestions = [];
        state.isOpen = false;
        return;
      }

      const query = (keyword || '').trim();
      state.keyword = query;
      if (!query) {
        state.suggestions = [];
        state.isOpen = false;
        return;
      }
      this.$nextTick(() => this.updateSuggestionPosition(row.id));
      this.getRowSearchDebouncer(row.id)(query);
    },
    onNameFocus(row, column) {
      this.removeInvalidMarker(column.field, row.id);
      const currentRow = this.data[row.id];
      if (!currentRow || currentRow.type !== PRODUCT_TYPE.PRODUCT) return;

      const query = (currentRow.name || '').trim();
      if (query) {
        const state = this.getProductSearchState(row.id);
        state.keyword = query;
        this.$nextTick(() => this.updateSuggestionPosition(row.id));
        this.getRowSearchDebouncer(row.id)(query);
        return;
      }

      this.fetchDefaultProductSuggestions(row.id);
    },
    getDefaultProducts() {
      if (this.defaultProductSuggestions.length > 0) {
        return Promise.resolve(this.defaultProductSuggestions);
      }
      if (this.defaultProductSuggestionsPromise) {
        return this.defaultProductSuggestionsPromise;
      }

      this.defaultProductSuggestionsPromise = new ProductProxy()
        .list({ page: 1, page_size: 5 })
        .then((res) => {
          this.defaultProductSuggestions = this.extractList(res);
          return this.defaultProductSuggestions;
        })
        .catch(() => [])
        .finally(() => {
          this.defaultProductSuggestionsPromise = null;
        });

      return this.defaultProductSuggestionsPromise;
    },
    fetchDefaultProductSuggestions(rowId) {
      const state = this.getProductSearchState(rowId);
      this.getDefaultProducts().then((list) => {
        state.suggestions = Array.isArray(list) ? list.slice(0, 5) : [];
        state.isOpen = state.suggestions.length > 0;
        if (state.isOpen) this.$nextTick(() => this.updateSuggestionPosition(rowId));
      });
    },
    onNameBlur(rowId) {
      setTimeout(() => {
        const state = this.getProductSearchState(rowId);
        state.isOpen = false;
      }, 120);
    },
    onNameEnter(row) {
      if (!row || row.type !== PRODUCT_TYPE.PRODUCT) return;
      const suggestions = this.getProductSuggestions(row.id);
      if (suggestions.length > 0) {
        this.selectProductSuggestion(row, suggestions[0]);
      }
    },
    getInputRefByRowId(rowId) {
      const ref = this.$refs[`name-input-${rowId}`];
      if (!ref) return null;
      return Array.isArray(ref) ? ref[0] : ref;
    },
    updateSuggestionPosition(rowId) {
      const state = this.getProductSearchState(rowId);
      const inputEl = this.getInputRefByRowId(rowId);
      if (!inputEl) return;
      const rect = inputEl.getBoundingClientRect();
      const viewportPadding = 12;
      const maxHeight = Math.max(160, Math.min(320, window.innerHeight - rect.bottom - viewportPadding));
      state.dropdownStyle = {
        position: 'fixed',
        left: `${Math.round(rect.left)}px`,
        right: 'auto',
        top: `${Math.round(rect.bottom + 2)}px`,
        width: `${Math.round(rect.width)}px`,
        maxHeight: `${maxHeight}px`,
        zIndex: 12000,
      };
    },
    getSuggestionDropdownStyle(rowId) {
      const state = this.getProductSearchState(rowId);
      return state.dropdownStyle || {};
    },
    refreshSuggestionPositions() {
      Object.keys(this.productSearchStates).forEach((key) => {
        const rowId = Number(key);
        const state = this.productSearchStates[rowId];
        if (state && state.isOpen) {
          this.updateSuggestionPosition(rowId);
        }
      });
    },
    searchProductsForRow(rowId, keyword) {
      const state = this.getProductSearchState(rowId);
      const query = (keyword || '').trim();
      if (!query) {
        state.suggestions = [];
        state.isOpen = false;
        return;
      }
      state.requestKey = query;
      new ProductProxy()
        .list({ search: query, page: 1, page_size: 20 })
        .then((res) => {
          if (state.requestKey !== query) return;
          state.suggestions = this.extractList(res);
          state.isOpen = state.suggestions.length > 0;
          if (state.isOpen) this.$nextTick(() => this.updateSuggestionPosition(rowId));
        })
        .catch(() => {
          if (state.requestKey !== query) return;
          state.suggestions = [];
          state.isOpen = false;
        });
    },
    shouldShowProductSuggestions(rowId) {
      const state = this.productSearchStates[rowId];
      return !!(state && state.isOpen && state.suggestions && state.suggestions.length);
    },
    getProductSuggestions(rowId) {
      const state = this.productSearchStates[rowId];
      return (state && state.suggestions) ? state.suggestions : [];
    },
    resolveCategory(product) {
      if (!product) return { id: null, name: '' };
      if (product.category_obj && typeof product.category_obj === 'object') {
        return {
          id: product.category_obj.id != null ? product.category_obj.id : null,
          name: product.category_obj.name || '',
        };
      }
      if (product.category && typeof product.category === 'object') {
        return {
          id: product.category.id != null ? product.category.id : null,
          name: product.category.name || '',
        };
      }
      if (product.category_id != null) {
        return { id: product.category_id, name: this.productCategoryMap[product.category_id] || '' };
      }
      if (typeof product.category === 'number') {
        return { id: product.category, name: this.productCategoryMap[product.category] || '' };
      }
      return { id: null, name: typeof product.category === 'string' ? product.category : '' };
    },
    normalizeRateValue(rate) {
      if (rate == null || rate === '') return { rate: 'NONE', custom_tax_rate: 0 };
      const numericRate = Number(rate);
      if (!Number.isNaN(numericRate)) {
        if ([0, 5, 8, 10].includes(numericRate)) return { rate: numericRate, custom_tax_rate: 0 };
        return { rate: 'KHAC', custom_tax_rate: numericRate };
      }
      const textRate = String(rate).trim().toUpperCase();
      if (['NONE', 'KKKNT', 'KHAC'].includes(textRate)) return { rate: textRate, custom_tax_rate: 0 };
      return { rate: 'NONE', custom_tax_rate: 0 };
    },
    selectProductSuggestion(row, product) {
      const currentRow = this.data[row.id];
      if (!currentRow) return;
      const category = this.resolveCategory(product);
      const normalizedRate = this.normalizeRateValue(product.rate);

      currentRow.name = product.name || '';
      currentRow.unit = product.unit || '';
      currentRow.price = Number(product.price || 0);
      currentRow.rate = normalizedRate.rate;
      currentRow.custom_tax_rate = normalizedRate.custom_tax_rate;
      currentRow.product_id = product.id || null;
      currentRow.category_id = category.id;
      currentRow.category_name = category.name;

      this.calculateSubTotal(currentRow, false, false);
      this.recalculateTotals();
      this.passDataToParent();

      const state = this.getProductSearchState(row.id);
      state.keyword = currentRow.name;
      state.isOpen = false;
      state.suggestions = [];
    },
    renderSuggestionMeta(product) {
      const category = this.resolveCategory(product);
      const categoryText = category.name ? `(${category.name})` : '';
      const unitText = product.unit ? ` - ${product.unit}` : '';
      const hasPrice = product.price !== null && product.price !== undefined && product.price !== '';
      const priceText = hasPrice
        ? ` - ${tempNumberFormatter(
          Number(product.price),
          this.handleMakeARound('UNIT_PRICE'),
          this.separatorFromSettings.thousandSeparator,
          this.separatorFromSettings.decimalSeparator
        )} ${this.currencyCode}`
        : '';
      return `${categoryText}${unitText}${priceText}`;
    },
    renderTypeCell(row) {
      const currentRow = this.data[row.id] || row;
      if (currentRow.type === PRODUCT_TYPE.PRODUCT && currentRow.category_name) {
        return currentRow.category_name;
      }
      return this.transformType(currentRow.type);
    },
    transformType(type) {
      return `${this.$t(`invoice.productTypes.${type}`) || type}`.toUpperCase();
    },
    onAddDecreeRow(newData) {
      return newData.concat({
        ...this.columns.reduce((obj, item) => {
          obj[item.field] = null;
          return obj;
        }, {}),
        name: '',
        id: newData.length,
        custom_tax_rate: 0,
        rate: 0,
        tax_money: 0,
        type: PRODUCT_TYPE.DECREE
      });
    },
    removeDecreeRowinTableData(newData) {
      const decreeIndex = newData.findIndex(row => row.type === PRODUCT_TYPE.DECREE)
      // Remove DECREE line if exists
      if (decreeIndex !== -1) {
        return newData
      }
      // Remove tax money of DECREE in totals before remove
      newData.splice(decreeIndex, 1);

      return newData
    },
    toggleTaxAmountInTable(newData, show = true) {
      if (!show) {
        newData = newData.map((row, id) => ({
          ...row,
          total: row.total - row.tax_money,
          tax_money: 0,
          rate: 0,
          custom_tax_rate: 0,
          id
        }));
      }
      return newData
    },
    isAdjustTaxOnlyMode() {
      return this.isAdjustment
        && this.data.length > 0
        && this.data.every(r => {
          if (r.type === PRODUCT_TYPE.NOTE || r.type === PRODUCT_TYPE.DECREE) return true;
          return Number(r.subtotal || 0) === 0;
        });
    }
  },
  watch: {
    invoiceType(newVal, oldVal) {
      let newData = Array.from(this.data);
      let newTotals = { ...this.totals };

      switch (oldVal) {
        case '1':
          // Update total (plus tax money)
          newTotals.total_amount_with_tax -= newTotals.total_tax_mount;
          newTotals.total_tax_mount = 0;
          newData = this.toggleTaxAmountInTable(newData, false)
          switch (newVal) {
            case '2':
              newData = this.onAddDecreeRow(newData);
              break;
            default: break;
          }
          break;
        case '2':
          newTotals.total_tax_mount = 0;
          newTotals.decree_tax_reduction = 0;

          switch (newVal) {
            case '1':
              newData = this.toggleTaxAmountInTable(newData, true)
              break;
            default: break;
          }
          newData = this.removeDecreeRowinTableData(newData);
          break;
        case '6':
          switch (newVal) {
            case '1':
              newData = this.toggleTaxAmountInTable(newData, true);
              break;
            case '2':
              newData = this.onAddDecreeRow(newData);
              break;
            default: break;
          }
        default:
          break;
      }

      this.totals = newTotals;
      // Emit ra parent
      this.passDataToParent(newData);
    },

    'totals.total_amount_without_tax'(newVal) {
      if (this.invoiceType === '2') {
        const decreeRow = this.data.find(r => r.type === PRODUCT_TYPE.DECREE);
        if (decreeRow) {
          this.calculateSubTotal(decreeRow);
          if (typeof this.recalculateTotals === 'function') {
            this.recalculateTotals();
          }
        }
      }
    },

    'totals.total_amount_with_tax'(newVal) {
      if (this.invoiceType === '1') {
        const decreeRow = this.data.find(r => r.type === PRODUCT_TYPE.DECREE);
        if (decreeRow) {
          this.calculateSubTotal(decreeRow);
          if (typeof this.recalculateTotals === 'function') {
            this.recalculateTotals();
          }
        }
      }
    },
  }
};
</script>
<style lang="scss">
.table-container {
  --text-sm: 0.875rem; // 14px
  --text-sm--line-height: calc(1.25 / 0.875);

  overflow: visible !important;

  #vgt-table {
    border: none !important;

    .form-group {
      label {
        display: none;
      }

      margin-bottom: 0;
    }

    td,
    th {
      padding: 0.5rem;
      border-left: none;
      border-right: none;
      border-bottom: none;
      font-size: var(--text-sm);
      line-height: var(--text-sm--line-height);

      input,
      select {
        font-size: var(--text-sm);
        line-height: var(--text-sm--line-height);
      }
    }

    tbody {
      td {
        vertical-align: middle;
        border: 0 !important;
        padding: .5rem .25rem;
        overflow: visible !important;
      }

      .line-numbers {
        border: none;
        background: unset;
        font-weight: normal;
        font-size: var(--text-sm);
        line-height: var(--text-sm--line-height);
      }
    }

    thead th {
      vertical-align: bottom !important;
      ;
      background: transparent !important;
      border: 0 !important;
      border-bottom: 0.5px solid rgba(0, 0, 0, 0.1) !important;
      color: #005DCA !important;

      span {
        font-size: var(--text-sm);
        line-height: var(--text-sm--line-height);
      }

      button {
        display: none;
      }
    }

    .form-group .input-currency-wrapper .currency-text-display,
    td span {
      font-size: var(--text-sm);
      line-height: var(--text-sm--line-height);
    }
  }

  .vgt-responsive {
    overflow-x: auto !important;
    overflow-y: visible !important;
    position: relative;
    z-index: 1;
  }

  .vgt-inner-wrap {
    overflow: visible !important;
  }

  .total-in-word {
    span {
      text-transform: uppercase;
      width: 50%;
      text-align: right;
    }
  }
}

.product-name-cell {
  position: relative;
}

.product-suggestion-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 2px);
  bottom: auto;
  z-index: 9999;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
}

.product-suggestion-item {
  padding: 6px 8px;
  cursor: pointer;
  font-size: 12px;
  line-height: 1.4;
}

.product-suggestion-item:hover {
  background: #f3f9ff;
}

.product-name-textarea {
  min-height: 38px;
  padding-top: 8px;
  padding-bottom: 8px;
  line-height: 1.4;
}


.btn-icon {
  background-color: rgb(220, 236, 252);
  border: none;
  color: white;
  padding: 12px 16px;
  font-size: 16px;
  cursor: pointer;
}

/* Darker background on mouse-over */
.btn-icon:hover {
  background-color: rgb(250, 223, 223);
}
</style>



// WEBPACK FOOTER //
// src/components/dynamic-table/dynamic-table.vue